import { financeKeys } from "@/modules/finances/gateway/financeKeys"
import { paymentsToValidateKeys } from "@/modules/finances/paymentsToValidate/gateway/paymentsToValidateKeys"
import {
  BoxOfferDetailedEntity,
  BoxOfferDraftEntity,
  BoxOfferItemEntity,
  BoxOfferMobileDetailedEntity,
  BoxOfferMobileItemEntity,
} from "@modules/boxOffer/entities/boxOfferEntity"
import { BoxOfferInvitationEntity } from "@modules/boxOffer/entities/boxOfferInvitationEntity"
import { type BoxOfferInvitationInput, StatusFilter } from "@modules/boxOffer/gateway/boxOfferDto"
import { useServicesContext } from "@modules/core/services/services.context"
import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from "@tanstack/react-query"

export const useBoxOffers = (options?: { onlyWithPrice?: boolean; statusFilter?: StatusFilter }) => {
  const { boxOfferApi } = useServicesContext()

  const { data, isLoading, isError } = useSuspenseQuery({
    queryKey: ["boxOffers", options],
    queryFn: async () => {
      return await boxOfferApi.getBoxOffers(options)
    },
  })

  return {
    data: data.map((boxOffer) => BoxOfferItemEntity.fromItemDto(boxOffer)),
    isLoading,
    isError,
  }
}

export const useBoxOffersByUser = (boxId: string, enabled: boolean) => {
  const { boxOfferApi } = useServicesContext()

  const { data, isLoading } = useQuery({
    queryKey: ["boxOffersUser", boxId],
    queryFn: async () => {
      return await boxOfferApi.getBoxOffersByUser(boxId)
    },
    enabled,
  })

  return {
    data: data?.map((boxOffer) => BoxOfferMobileItemEntity.fromDto(boxOffer)) || [],
    isLoading,
  }
}

export const useBoxOfferByUser = (boxId: string, offerId: string) => {
  const { boxOfferApi } = useServicesContext()

  const { data, isLoading } = useQuery({
    queryKey: ["boxOfferUser", offerId, boxId],
    queryFn: async () => {
      return await boxOfferApi.getBoxOfferByUser(boxId, offerId)
    },
  })

  return {
    data: data ? BoxOfferMobileDetailedEntity.fromDto(data) : null,
    isLoading,
  }
}

export const useBoxOffer = (id?: string) => {
  const { boxOfferApi } = useServicesContext()
  const queryClient = useQueryClient()

  const { data, isLoading } = useQuery({
    queryKey: ["boxOffer", id],
    queryFn: async () => {
      if (!id) return null
      return await boxOfferApi.getBoxOffer(id)
    },
  })

  // Create offer
  const { mutateAsync: createBoxOffer, isPending: isCreateOffer } = useMutation({
    mutationFn: async (offer: BoxOfferDraftEntity) => {
      return await boxOfferApi.createBoxOffer(offer)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["boxOffers"],
      })
    },
  })

  // Update offer
  const { mutateAsync: updateOffer, isPending: isUpdateOffer } = useMutation({
    mutationFn: async (offer: BoxOfferDraftEntity) => {
      if (!id) throw new Error("ID is required to update the offer")
      return await boxOfferApi.updateBoxOffer(id, offer)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["boxOffers"],
      })
      queryClient.invalidateQueries({ queryKey: ["boxOffer", id] })
    },
  })

  // Delete offer
  const { mutateAsync: deleteBoxOffer, isPending: isDeleteOffer } = useMutation({
    mutationFn: async () => {
      if (!id) throw new Error("ID is required to delete the offer")
      return await boxOfferApi.deleteBoxOffer(id)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["boxOffers"],
      })
      queryClient.invalidateQueries({ queryKey: ["boxOffer", id] })
    },
  })

  const isPending = isUpdateOffer || isCreateOffer || isDeleteOffer

  return {
    data: data ? BoxOfferDetailedEntity.fromDto(data) : null,
    isLoading,
    isPending,
    updateOffer,
    createBoxOffer,
    deleteBoxOffer,
  }
}

export const useBoxOfferInvitation = (boxOfferInvitationId: string, userId: string) => {
  const { boxOfferApi } = useServicesContext()
  const queryClient = useQueryClient()

  // Cancel box offer invitation by owner
  const { mutateAsync: cancelBoxOfferInvitation, isPending: isCancelBoxOfferInvitation } = useMutation({
    mutationFn: async () => {
      return await boxOfferApi.cancelBoxOfferInvitation(boxOfferInvitationId)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["boxOfferInvitations", userId],
      })
    },
  })

  return {
    cancelBoxOfferInvitation,
    isCancelBoxOfferInvitation,
  }
}

export const useBoxOfferInvitations = (userId: string, memberId?: string) => {
  const { boxOfferApi } = useServicesContext()
  const queryClient = useQueryClient()

  // Get box offer invitations
  const { data } = useSuspenseQuery({
    queryKey: ["boxOfferInvitations", userId],
    queryFn: async () => {
      return await boxOfferApi.getBoxOfferInvitations(userId)
    },
  })
  // Send box offer invitation
  const { mutateAsync: sendBoxOfferInvitation, isPending: isSendBoxOfferInvitation } = useMutation({
    mutationFn: async (invitationInput: BoxOfferInvitationInput) => {
      return await boxOfferApi.sendBoxOfferInvitation(invitationInput)
    },
    onSuccess: () => {
      setTimeout(() => {
        queryClient.invalidateQueries({
          queryKey: ["boxOfferInvitations", userId],
        })

        queryClient.invalidateQueries({
          queryKey: ["boxArchivedMembers"],
        })
        queryClient.invalidateQueries({
          queryKey: ["boxMembers"],
        })
        queryClient.invalidateQueries({
          queryKey: ["boxLeads"],
        })

        if (memberId) {
          queryClient.invalidateQueries({
            queryKey: ["boxMember", memberId],
          })
          queryClient.invalidateQueries({
            queryKey: ["boxPurchases", memberId],
          })
          queryClient.invalidateQueries({
            queryKey: paymentsToValidateKeys.all,
          })
          queryClient.invalidateQueries({
            queryKey: financeKeys.all,
          })
        }
      }, 2000) // Delay to ensure the data updates correctly
    },
  })

  return {
    data: data.map((invitation) => BoxOfferInvitationEntity.fromDto(invitation)) || [],
    sendBoxOfferInvitation,
    isSendBoxOfferInvitation,
  }
}

export const useUpdateOffersOrder = () => {
  const { boxOfferApi } = useServicesContext()
  const queryClient = useQueryClient()

  const { mutateAsync: saveOrder, isPending } = useMutation({
    mutationFn: async (visibleOrder: string[]) => {
      const orderMap: Record<string, number> = {}
      let position = 1
      visibleOrder.forEach((id) => {
        orderMap[id] = position
        position += 1
      })
      await boxOfferApi.arrangeOffersDisplayOrder(orderMap)
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["boxOffers"] })
    },
  })

  return {
    saveOrder,
    isPending,
  }
}
